import React, { useState } from 'react';

import { format } from 'date-fns';

import styled from 'styled-components';

import aampeTheme from '../../constants/aampeTheme';

import {
    getDiffRows,
    getDiffToggleLabel,
    getTargetLabel,
    isOpaqueValue,
    isStructuredValue,
    parseBackendDate,
} from './helpers';

import {
    ActivityLogModelType,
    ActivityLogPart,
    ActivityLogType,
    ActivityLogTypeName,
    ActivtyLog,
    getModelName,
    isActivityLogChange,
} from 'models/activty-log.model';

import AuditChangeTable, {
    After,
    Arrow,
    Before,
    ChangeValue,
    Field,
    fieldLabel,
} from './AuditChangeTable';
import {
    AuditRefs,
    EMPTY_AUDIT_REFS,
    formatListValue,
    getEntryScope,
    visibleSnapshotFields,
} from './references';

export const buildEntrySummary = (log: ActivtyLog, userLabel: string) =>
    `${getModelName(log.object_type)} ${
        ActivityLogTypeName[log.verb] || log.verb
    } by ${userLabel}`;

type AuditTrailEntryProps = {
    log: ActivtyLog;
    userLabel: string;
    summary?: string;
    showTarget?: boolean;
    targetName?: string;
    refs?: AuditRefs;
};

/** Theme pairings per action, so a column of entries can be scanned by colour. */
const VERB_TONES: Record<string, { ink: string; wash: string }> = {
    [ActivityLogType.CREATE]: {
        ink: aampeTheme.colors.textAccessibleSG01,
        wash: aampeTheme.colors.backgroundSG02,
    },
    [ActivityLogType.UPDATE]: {
        ink: aampeTheme.colors.blue700,
        wash: aampeTheme.colors.interactiveA01,
    },
    [ActivityLogType.DELETE]: {
        ink: aampeTheme.colors.error700,
        wash: aampeTheme.colors.error50,
    },
    [ActivityLogType.ARCHIVE]: {
        ink: aampeTheme.colors.textAccessibleWG01,
        wash: aampeTheme.colors.backgroundWG02,
    },
};

const verbLabel = (verb: ActivtyLog['verb']) => {
    const name = ActivityLogTypeName[verb] ?? String(verb).toLowerCase();
    return name.charAt(0).toUpperCase() + name.slice(1);
};

export { fieldLabel };

/** The title already names the type, so keep only what the label adds. */
const contextLabel = (label: string, title: string): string | null => {
    if (label === title) return null;
    return label.startsWith(`${title}: `)
        ? label.slice(title.length + 2)
        : label;
};

const text = (v: unknown) =>
    typeof v === 'string' && v.trim() ? v : undefined;

/** "Variant: Urgency", "Message content: English": parts carry labels, not names. */
const partTitle = (part: ActivityLogPart) => {
    const snapshot = part.after ?? part.before ?? {};
    const label =
        part.object_type === ActivityLogModelType.VARIANT
            ? text(snapshot.label_name) ?? text(snapshot.component_key)
            : part.object_type === ActivityLogModelType.MESSAGE_CONTENT
            ? text(snapshot.language_name)
            : text(snapshot.name);
    const type = getModelName(part.object_type);
    return label ? `${type}: ${label}` : type;
};

const AuditTrailEntry: React.FC<AuditTrailEntryProps> = ({
    log,
    userLabel,
    summary,
    showTarget,
    targetName,
    refs = EMPTY_AUDIT_REFS,
}) => {
    const [expanded, setExpanded] = useState(false);
    const change = isActivityLogChange(log.data) ? log.data : null;
    const diff = change ? getDiffRows(change) : null;
    const parts = change?.parts ?? [];
    const title = summary ?? getModelName(log.object_type);
    const scope = getEntryScope(log, refs);
    const rawTarget = showTarget
        ? contextLabel(targetName ?? getTargetLabel(log, refs), title)
        : null;
    // A frequency limit's scope already names its trigger.
    const targetLabel =
        rawTarget && scope?.startsWith(rawTarget) ? null : rawTarget;
    const createdAt = parseBackendDate(log.created_at);
    const source = {
        activityId: log.id,
        firstActivityId: log.first_activity_id ?? log.id,
    };

    const snapshotRow = change ? change.after ?? change.before : null;
    const visible = new Set(
        visibleSnapshotFields(
            diff?.rows.map((row) => row.field) ?? [],
            snapshotRow,
        ),
    );
    const rows =
        diff?.rows.filter(
            (row) => diff.mode === 'diff' || visible.has(row.field),
        ) ?? [];

    // One plain value changed (and nothing else): show it in the row.
    const inline =
        change &&
        parts.length === 0 &&
        diff?.mode === 'diff' &&
        rows.length === 1 &&
        !formatListValue(log.object_type, rows[0].field, rows[0].after, refs) &&
        !isStructuredValue(rows[0].before) &&
        !isStructuredValue(rows[0].after) &&
        !isOpaqueValue(rows[0].before) &&
        !isOpaqueValue(rows[0].after)
            ? rows[0]
            : null;

    const tone = VERB_TONES[log.verb] ?? VERB_TONES[ActivityLogType.ARCHIVE];
    // A merged run whose edits cancelled out: nothing changed on the object
    // itself or on any of its parts.
    const noNetChange = Boolean(
        change &&
            change.changed_fields.length === 0 &&
            parts.length === 0 &&
            (diff?.mode === 'diff' || rows.length === 0),
    );
    const expandable = Boolean(
        change && !inline && (rows.length > 0 || parts.length > 0),
    );
    const hasDetails = Boolean(inline || expandable || noNetChange);
    const savesLabel =
        (log.save_count ?? 1) > 1 && log.first_created_at
            ? `${log.save_count} saves from ${format(
                  parseBackendDate(log.first_created_at),
                  'hh:mm a',
              )}`
            : null;

    return (
        <Entry aria-label={summary ?? buildEntrySummary(log, userLabel)}>
            <Time dateTime={createdAt.toISOString()}>
                {format(createdAt, 'hh:mm a')}
            </Time>
            <Badge $ink={tone.ink} $wash={tone.wash}>
                {verbLabel(log.verb)}
            </Badge>
            <TitleLine>
                <Title>{title}</Title>
                {targetLabel && <Context>{targetLabel}</Context>}
                {scope && <Context>{scope}</Context>}
                {savesLabel && <Context>{savesLabel}</Context>}
            </TitleLine>
            {!summary && <Who data-area="who">{userLabel}</Who>}
            {hasDetails && change && (
                <Details>
                    {noNetChange && (
                        <Context>
                            No net change: later edits undid earlier ones.
                        </Context>
                    )}
                    {inline && change.before && change.after && (
                        <InlineChange>
                            <Field>{fieldLabel(inline.field)}</Field>
                            <Before>
                                <ChangeValue
                                    objectType={log.object_type}
                                    field={inline.field}
                                    value={inline.before}
                                    row={change.before}
                                    refs={refs}
                                />
                            </Before>
                            <Arrow aria-hidden>→</Arrow>
                            <After>
                                <ChangeValue
                                    objectType={log.object_type}
                                    field={inline.field}
                                    value={inline.after}
                                    row={change.after}
                                    refs={refs}
                                />
                            </After>
                        </InlineChange>
                    )}
                    {expandable && (
                        <>
                            <DiffToggle
                                type="button"
                                aria-expanded={expanded}
                                onClick={() => setExpanded((v) => !v)}
                            >
                                {expanded ? '▾' : '▸'}{' '}
                                {getDiffToggleLabel(change)}
                            </DiffToggle>
                            {expanded && (
                                <>
                                    <AuditChangeTable
                                        objectType={log.object_type}
                                        change={change}
                                        refs={refs}
                                        source={source}
                                    />
                                    {parts.map((part) => (
                                        <Part
                                            key={`${part.object_type}-${part.object_id}`}
                                        >
                                            <PartHeading>
                                                <span>{partTitle(part)}</span>
                                                {part.verb !==
                                                    ActivityLogType.UPDATE && (
                                                    <Context>
                                                        {ActivityLogTypeName[
                                                            part.verb as ActivityLogType
                                                        ] ??
                                                            String(
                                                                part.verb,
                                                            ).toLowerCase()}
                                                    </Context>
                                                )}
                                            </PartHeading>
                                            <AuditChangeTable
                                                objectType={part.object_type}
                                                change={part}
                                                refs={refs}
                                                source={{
                                                    activityId:
                                                        part.activity_id,
                                                    firstActivityId:
                                                        part.first_activity_id,
                                                }}
                                            />
                                        </Part>
                                    ))}
                                </>
                            )}
                        </>
                    )}
                </Details>
            )}
        </Entry>
    );
};

export default AuditTrailEntry;

const Entry = styled.li({
    display: 'grid',
    gridTemplateColumns: '68px 76px minmax(0, 1fr) auto',
    gridTemplateAreas: `
        "time badge title who"
        ".    .     details details"
    `,
    columnGap: aampeTheme.spacing4(3),
    rowGap: aampeTheme.spacing4(1),
    alignItems: 'baseline',
    padding: aampeTheme.spacing4(2.5, 0),
    borderTop: `1px solid ${aampeTheme.colors.interactiveWG01}`,
    listStyle: 'none',
    '&:first-of-type': { borderTop: 'none' },
    // Narrow hosts (history drawers): the person drops under the title.
    '@container (max-width: 520px)': {
        gridTemplateColumns: '60px 72px minmax(0, 1fr)',
        gridTemplateAreas: `
            "time badge title"
            ".    .     who"
            ".    .     details"
        `,
        '& > [data-area="who"]': { textAlign: 'left' },
    },
});

const Time = styled.time({
    gridArea: 'time',
    fontSize: aampeTheme.size.font.textMedium,
    color: aampeTheme.colors.textAccessibleWG01,
    fontVariantNumeric: 'tabular-nums',
    whiteSpace: 'nowrap',
});

const Badge = styled.span<{ $ink: string; $wash: string }>(
    ({ $ink, $wash }) => ({
        gridArea: 'badge',
        justifySelf: 'start',
        padding: aampeTheme.spacing4(0.5, 2),
        borderRadius: aampeTheme.borderRadius.small,
        backgroundColor: $wash,
        color: $ink,
        fontSize: aampeTheme.size.font.textMedium,
        fontWeight: aampeTheme.weights.medium,
        lineHeight: aampeTheme.size.lineHeight.small,
        whiteSpace: 'nowrap',
    }),
);

const Details = styled.div({
    gridArea: 'details',
    display: 'flex',
    flexDirection: 'column',
    gap: aampeTheme.spacing4(1),
    minWidth: 0,
});

const TitleLine = styled.div({
    gridArea: 'title',
    minWidth: 0,
    display: 'flex',
    flexWrap: 'wrap',
    alignItems: 'baseline',
    columnGap: aampeTheme.spacing4(2),
    rowGap: aampeTheme.spacing4(0.5),
});

const Title = styled.span({
    fontSize: aampeTheme.size.font.default,
    fontWeight: aampeTheme.weights.medium,
    color: aampeTheme.colors.textAccessibleWG02,
    lineHeight: aampeTheme.size.lineHeight.small,
});

const Context = styled.span({
    fontSize: aampeTheme.size.font.textMedium,
    color: aampeTheme.colors.textAccessibleWG01,
});

const Who = styled.span({
    gridArea: 'who',
    fontSize: aampeTheme.size.font.textMedium,
    color: aampeTheme.colors.textAccessibleWG01,
    textAlign: 'right',
    overflowWrap: 'anywhere',
});

const InlineChange = styled.div({
    display: 'flex',
    flexWrap: 'wrap',
    alignItems: 'baseline',
    gap: aampeTheme.spacing4(2),
    fontSize: aampeTheme.size.font.textMedium,
});

const DiffToggle = styled.button({
    alignSelf: 'flex-start',
    background: 'none',
    border: 'none',
    padding: 0,
    cursor: 'pointer',
    fontSize: aampeTheme.size.font.textMedium,
    color: aampeTheme.colors.textAccessibleWG01,
    '&:hover': { color: aampeTheme.colors.textAccessibleWG02 },
    '&:focus-visible': {
        outline: `2px solid ${aampeTheme.colors.solidA01}`,
        outlineOffset: '2px',
    },
});

const Part = styled.section({
    display: 'flex',
    flexDirection: 'column',
    gap: aampeTheme.spacing4(1),
});

const PartHeading = styled.div({
    display: 'flex',
    alignItems: 'baseline',
    gap: aampeTheme.spacing4(2),
    fontSize: aampeTheme.size.font.textMedium,
    fontWeight: aampeTheme.weights.medium,
    color: aampeTheme.colors.textAccessibleWG02,
});
